<script setup>
import { onMounted, onBeforeUnmount, ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import KanbanColumn from './KanbanColumn.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

const store = useStore();
const { t } = useI18n();

const isLoading = ref(false);
const showDisplaySettings = ref(false);
const kanbanColumnsRef = ref(null);
const isDraggingColumns = ref(false);

const DEFAULT_VISIBLE_FIELDS = [
  'lastMessage',
  'inbox',
  'createdAt',
  'unreadCount',
];
const DEFAULT_PREFERENCES = {
  visibleFields: DEFAULT_VISIBLE_FIELDS,
  monetaryAttributeKey: '',
  selectedInboxId: '',
};

const kanbanPreferences = ref({ ...DEFAULT_PREFERENCES });

const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const storageKey = computed(() => String(currentAccountId.value || 'global'));

const kanbanLabels = computed(
  () => store.getters['kanban/getKanbanLabels'] || []
);
const inboxOptions = computed(() => {
  const inboxes = store.getters['inboxes/getInboxes'] || [];

  return inboxes.map(inbox => ({
    id: String(inbox.id),
    name: inbox.name,
  }));
});

const untaggedConversations = computed(
  () => store.getters['kanban/getUntaggedConversations'] || []
);

const conversationAttributes = computed(
  () => store.getters['attributes/getConversationAttributes'] || []
);

const monetaryAttributeOptions = computed(() =>
  conversationAttributes.value.filter(
    attribute => attribute.attributeDisplayType === 'number'
  )
);

const selectedMonetaryAttribute = computed(
  () =>
    monetaryAttributeOptions.value.find(
      attribute =>
        attribute.attributeKey === kanbanPreferences.value.monetaryAttributeKey
    ) || null
);

const visibleFieldOptions = computed(() => [
  {
    key: 'lastMessage',
    label: t('KANBAN.SHOW_LAST_MESSAGE'),
  },
  {
    key: 'inbox',
    label: t('KANBAN.SHOW_INBOX'),
  },
  {
    key: 'createdAt',
    label: t('KANBAN.SHOW_CREATED_AT'),
  },
  {
    key: 'unreadCount',
    label: t('KANBAN.SHOW_UNREAD_COUNT'),
  },
]);

const normalizedPreferences = preferences => {
  const visibleFields = Array.isArray(preferences?.visibleFields)
    ? DEFAULT_VISIBLE_FIELDS.filter(field =>
        preferences.visibleFields.includes(field)
      )
    : DEFAULT_VISIBLE_FIELDS;

  return {
    visibleFields,
    monetaryAttributeKey: preferences?.monetaryAttributeKey || '',
    selectedInboxId: preferences?.selectedInboxId || '',
  };
};

const loadPreferences = () => {
  const storedPreferences = LocalStorage.getFromJsonStore(
    LOCAL_STORAGE_KEYS.KANBAN_PREFERENCES,
    storageKey.value
  );

  kanbanPreferences.value = normalizedPreferences(storedPreferences);
};

const selectedInboxFilter = computed(() =>
  kanbanPreferences.value.selectedInboxId
    ? { inbox_id: kanbanPreferences.value.selectedInboxId }
    : {}
);

async function loadKanbanData() {
  isLoading.value = true;
  try {
    await store.dispatch('kanban/loadKanbanData', selectedInboxFilter.value);
  } catch (error) {
    useAlert(t('KANBAN.ERROR_LOADING_DATA'));
  } finally {
    isLoading.value = false;
  }
}

const ensureConversationAttributes = async () => {
  if (conversationAttributes.value.length) {
    return;
  }

  await store.dispatch('attributes/get');
};

const ensureInboxes = async () => {
  const inboxes = store.getters['inboxes/getInboxes'] || [];
  if (inboxes.length) {
    return;
  }

  await store.dispatch('inboxes/get');
};

onMounted(async () => {
  loadPreferences();
  await Promise.all([
    loadKanbanData(),
    ensureConversationAttributes(),
    ensureInboxes(),
  ]);
});

watch(storageKey, () => {
  loadPreferences();
});

watch(
  kanbanPreferences,
  preferences => {
    LocalStorage.updateJsonStore(
      LOCAL_STORAGE_KEYS.KANBAN_PREFERENCES,
      storageKey.value,
      preferences
    );
  },
  { deep: true }
);

watch(
  () => kanbanPreferences.value.selectedInboxId,
  async () => {
    await loadKanbanData();
  }
);

const getConversationsByLabel = labelId => {
  const getter = store.getters['kanban/getConversationsByLabel'];
  return getter ? getter(labelId) : [];
};

const handleDrop = async data => {
  const { conversationId, fromLabelId, labelId, labelTitle, toIndex } = data;

  try {
    await store.dispatch('kanban/moveConversationToLabel', {
      conversationId,
      fromLabelId,
      labelId,
      labelTitle,
      toIndex,
    });

    useAlert(t('KANBAN.CONVERSATION_MOVED'));
  } catch (error) {
    useAlert(t('KANBAN.ERROR_MOVING_CONVERSATION'));
  }
};

const refreshData = async () => {
  await loadKanbanData();
  useAlert(t('KANBAN.DATA_REFRESHED'));
};

const initialContactColumn = computed(() => ({
  id: 'untagged',
  title: t('KANBAN.INITIAL_CONTACT'),
  color: '#9CA3AF',
}));

const toggleVisibleField = fieldKey => {
  const visibleFields = kanbanPreferences.value.visibleFields.includes(fieldKey)
    ? kanbanPreferences.value.visibleFields.filter(field => field !== fieldKey)
    : [...kanbanPreferences.value.visibleFields, fieldKey];

  kanbanPreferences.value = {
    ...kanbanPreferences.value,
    visibleFields,
  };
};

const dragState = {
  startX: 0,
  scrollLeft: 0,
};

const shouldIgnoreDragScroll = event => {
  const interactiveSelector =
    'button, select, input, textarea, a, [role="button"], [draggable="true"]';

  return Boolean(event.target.closest(interactiveSelector));
};

const handleColumnsMouseDown = event => {
  if (event.button !== 0 || shouldIgnoreDragScroll(event)) {
    return;
  }

  const container = kanbanColumnsRef.value;
  if (!container) {
    return;
  }

  isDraggingColumns.value = true;
  dragState.startX = event.pageX - container.offsetLeft;
  dragState.scrollLeft = container.scrollLeft;
};

const handleColumnsMouseMove = event => {
  if (!isDraggingColumns.value) {
    return;
  }

  const container = kanbanColumnsRef.value;
  if (!container) {
    return;
  }

  const currentX = event.pageX - container.offsetLeft;
  const walk = currentX - dragState.startX;

  container.scrollLeft = dragState.scrollLeft - walk;
};

const stopColumnsDragging = () => {
  isDraggingColumns.value = false;
};

onBeforeUnmount(() => {
  stopColumnsDragging();
});
</script>

<template>
  <div
    class="flex h-full min-h-0 w-full flex-1 flex-col bg-[#f7f8fb] dark:bg-n-background"
  >
    <!-- Header -->
    <div
      class="flex flex-shrink-0 items-center justify-between border-b border-n-weak bg-white px-6 py-4 dark:bg-n-solid-2"
    >
      <div>
        <select
          v-model="kanbanPreferences.selectedInboxId"
          class="min-w-[18rem] rounded-lg border border-n-weak bg-white px-3 py-2 text-base font-semibold text-n-slate-12 dark:bg-n-solid-3"
        >
          <option value="">{{ t('KANBAN.ALL_INBOXES') }}</option>
          <option
            v-for="inbox in inboxOptions"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.name }}
          </option>
        </select>
      </div>

      <div class="relative flex gap-2">
        <Button
          icon="i-lucide-refresh-cw"
          color="slate"
          size="sm"
          :disabled="isLoading"
          @click="refreshData"
        >
          {{ t('COMMON.REFRESH') }}
        </Button>
        <Button
          icon="i-lucide-sliders-horizontal"
          color="slate"
          size="sm"
          @click="showDisplaySettings = !showDisplaySettings"
        >
          {{ t('KANBAN.SETTINGS') }}
        </Button>

        <div
          v-if="showDisplaySettings"
          class="absolute right-0 top-full z-20 mt-2 w-80 rounded-xl border border-n-weak bg-white p-4 shadow-lg dark:bg-n-solid-2"
        >
          <div class="space-y-4">
            <div>
              <p
                class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.CARD_FIELDS') }}
              </p>
              <div class="mt-3 space-y-2">
                <label
                  v-for="field in visibleFieldOptions"
                  :key="field.key"
                  class="flex items-center gap-2 text-sm text-n-slate-12"
                >
                  <input
                    type="checkbox"
                    class="rounded border-n-weak text-n-brand"
                    :checked="
                      kanbanPreferences.visibleFields.includes(field.key)
                    "
                    @change="toggleVisibleField(field.key)"
                  />
                  <span>{{ field.label }}</span>
                </label>
              </div>
            </div>

            <div>
              <label
                class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.MONETARY_ATTRIBUTE') }}
              </label>
              <select
                v-model="kanbanPreferences.monetaryAttributeKey"
                class="mt-2 w-full rounded-lg border border-n-weak bg-[#f7f8fb] px-3 py-2 text-sm text-n-slate-12 dark:bg-n-solid-3"
              >
                <option value="">{{ t('KANBAN.NONE') }}</option>
                <option
                  v-for="attribute in monetaryAttributeOptions"
                  :key="attribute.attributeKey"
                  :value="attribute.attributeKey"
                >
                  {{ attribute.attributeDisplayName }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="isLoading" class="flex flex-1 items-center justify-center">
      <div class="text-center">
        <div
          class="i-lucide-loader mx-auto mb-4 size-8 animate-spin text-n-slate-9"
        />
        <p class="text-n-slate-10">{{ t('COMMON.LOADING') }}</p>
      </div>
    </div>

    <!-- Kanban board -->
    <div
      v-else
      ref="kanbanColumnsRef"
      class="flex-1 overflow-x-auto overflow-y-hidden p-6"
      :class="isDraggingColumns ? 'cursor-grabbing select-none' : 'cursor-grab'"
      @mousedown="handleColumnsMouseDown"
      @mousemove="handleColumnsMouseMove"
      @mouseup="stopColumnsDragging"
      @mouseleave="stopColumnsDragging"
    >
      <div class="flex h-full min-w-min gap-6 pb-4">
        <KanbanColumn
          :label="initialContactColumn"
          :conversations="untaggedConversations"
          :visible-fields="kanbanPreferences.visibleFields"
          :monetary-attribute-key="selectedMonetaryAttribute?.attributeKey"
          :monetary-attribute-label="
            selectedMonetaryAttribute?.attributeDisplayName
          "
          @drop="handleDrop"
        />

        <!-- Colunas com labels -->
        <KanbanColumn
          v-for="label in kanbanLabels"
          :key="label.id"
          :label="label"
          :conversations="getConversationsByLabel(label.id)"
          :visible-fields="kanbanPreferences.visibleFields"
          :monetary-attribute-key="selectedMonetaryAttribute?.attributeKey"
          :monetary-attribute-label="
            selectedMonetaryAttribute?.attributeDisplayName
          "
          @drop="handleDrop"
        />

        <!-- Estado vazio -->
        <div
          v-if="
            kanbanLabels.length === 0 &&
            untaggedConversations.length === 0 &&
            !isLoading
          "
          class="flex flex-1 items-center justify-center text-center"
        >
          <div>
            <div class="i-lucide-inbox size-12 mx-auto mb-4 text-n-slate-8" />
            <h3 class="mb-2 text-lg font-semibold text-n-slate-12">
              {{ t('KANBAN.NO_LABELS') || 'Nenhuma etiqueta disponível' }}
            </h3>
            <p class="max-w-md text-n-slate-10">
              {{
                t('KANBAN.CREATE_LABELS_HINT') ||
                'Crie etiquetas nas configurações para começar a usar o kanban'
              }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
